import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
    Upload, Search, FileText, Loader2, RefreshCw, AlertCircle, Plus, Repeat,
    Image as ImageIcon, FileType, Sparkles, CheckCircle2, XCircle,
    Clock, CreditCard, Archive, Check, Minus, Trash2, Landmark,
    Printer, Mail, SendHorizontal, Banknote,
} from 'lucide-react';
import { ACCENT as SECTION_COLOR, OK, WARN, BAD } from '../lib/uiTokens';
import ListToolbar from '../components/layout/ListToolbar';
import { FiltrTlacitko, AktivniFiltry } from '../components/layout/ListFiltr';
import DokladyUploadModal from './DokladyUploadModal';
import OdeslatMailemModal from '../components/doklady/OdeslatMailemModal';
import ExportAboModal from '../components/doklady/ExportAboModal';
import NabidkaAkci from '../components/layout/NabidkaAkci';
import { cenikShrnuti } from '../utils/cenik';

const fmt = (v) => {
    if (v == null) return '—';
    return new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
};

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('cs-CZ') : '—');

// Do seznamu bez haléřů — v přehledu jsou balast — ale s měnou, jinak je
// číslo dvojznačné (částka? počet?).
const fmtKratce = (v) => (v == null ? '—'
    : new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 0 }).format(v) + ' Kč');

// 1 / 2–4 / 5+ — české skloňování pro hlášky hromadných akcí.
const sklonuj = (n, [jeden, dva, hodne]) => (n === 1 ? jeden : n < 5 ? dva : hodne);

// Definice stavů včetně barev a ikon — sdílené napříč Inbox/Detail.
export const STAV_CONFIG = {
    nahrany:        { label: 'Nahraný',         color: '#94a3b8', bg: 'rgba(148,163,184,0.12)', Icon: Upload },
    skenuje_se:     { label: 'Skenuje se…',     color: '#3B82F6', bg: 'rgba(59,130,246,0.12)',  Icon: Loader2, spin: true },
    rozpoznan:      { label: 'Rozpoznán',       color: '#06b6d4', bg: 'rgba(6,182,212,0.12)',   Icon: Sparkles },
    k_doplneni:     { label: 'K doplnění',      color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',  Icon: AlertCircle },
    ke_schvaleni:   { label: 'Ke schválení',    color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)',  Icon: Clock },
    schvaleny:      { label: 'Schválený',       color: '#14b8a6', bg: 'rgba(20,184,166,0.12)',  Icon: Check },
    zamitnuty:      { label: 'Zamítnutý',       color: '#ef4444', bg: 'rgba(239,68,68,0.12)',   Icon: XCircle },
    ceka_na_platbu: { label: 'Čeká na platbu',  color: '#f97316', bg: 'rgba(249,115,22,0.14)',  Icon: CreditCard },
    zaplaceny:      { label: 'Zaplacený',       color: '#10b981', bg: 'rgba(16,185,129,0.16)',  Icon: CheckCircle2 },
    zauctovany:     { label: 'Zaúčtovaný',      color: '#64748b', bg: 'rgba(100,116,139,0.16)', Icon: Archive },
};

/**
 * Fáze dokladu pro seznam — jen cesta schvalováním (2026-10-14).
 *
 * V řádku bývalo deset stavů a k nim ikony banky, zadané platby, původu,
 * tisku a jistoty AI; nakonec už ikona nic neříkala. Seznam teď ukazuje jen,
 * kde doklad na cestě je. Všechno ostatní (k tisku, k zadání do banky,
 * AI nejistá, po splatnosti…) se hledá přes pohledy a filtry. Jemné stavy
 * (`STAV_CONFIG`) zůstávají v historii dokladu.
 */
export const FAZE_CONFIG = {
    zpracovani:     { label: 'Zpracovává se',  color: '#94a3b8', bg: 'rgba(148,163,184,0.14)', Icon: Loader2, spin: true },
    ke_schvaleni:   { label: 'Ke schválení',   color: '#8b5cf6', bg: 'rgba(139,92,246,0.14)',  Icon: Clock },
    ceka_na_platbu: { label: 'Čeká na platbu', color: '#f97316', bg: 'rgba(249,115,22,0.14)',  Icon: Banknote },
    zaplaceno:      { label: 'Zaplaceno',      color: '#10b981', bg: 'rgba(16,185,129,0.16)',  Icon: CheckCircle2 },
    uzavreno:       { label: 'Uzavřeno',       color: '#64748b', bg: 'rgba(100,116,139,0.16)', Icon: Archive },
};

const STAV_NA_FAZI = {
    nahrany: 'zpracovani', skenuje_se: 'zpracovani',
    rozpoznan: 'ke_schvaleni', k_doplneni: 'ke_schvaleni', ke_schvaleni: 'ke_schvaleni',
    schvaleny: 'ceka_na_platbu', ceka_na_platbu: 'ceka_na_platbu',
    zaplaceny: 'zaplaceno',
    zauctovany: 'uzavreno', zamitnuty: 'uzavreno',
};

/** Fáze dokladu; zamítnutý je uzavřený, jen s vlastním popiskem. */
export function fazeDokladu(stav) {
    const cfg = FAZE_CONFIG[STAV_NA_FAZI[stav]] || FAZE_CONFIG.zpracovani;
    if (stav === 'zamitnuty') return { ...cfg, label: 'Zamítnuto', Icon: XCircle };
    // Animace jen když opravdu skenuje — nahraný doklad čeká ve frontě.
    if (stav === 'nahrany') return { ...cfg, Icon: Upload, spin: false };
    return cfg;
}

/**
 * Pohledy = předem daná sada, vždy je aktivní právě jeden (norma UI).
 *
 * Řazené podle toho, co po člověku něco chce: nejdřív fronty, kde se čeká na
 * rozhodnutí, pak hotové věci, nakonec vše. `K tisku` není stav, ale fyzická
 * podoba — je to pohled, protože je to stejná otázka („co mám na stole?"),
 * jen se ptá jinam. Stavy `nahraný`, `skenuje se`, `rozpoznán` a `zamítnutý`
 * vlastní pohled nemají; jsou průchozí nebo koncové a dostaneš se k nim přes
 * „Vše" a hledání.
 */
export const POHLEDY = [
    // `skupina` = otázka, na kterou pohled odpovídá. Deset pohledů vedle sebe
    // v jedné řadě vypadalo jako deset rovnocenných věcí, přitom se ptají na
    // tři různé věci — a podle skupin je teď řadí nabídka (`PohledOvladac`).
    //
    // Zkratku v liště dostane jen pohled, který má `upozorneni` **a** `barva`;
    // ostatní jsou v nabídce. Je to vědomý rozpočet: co hlásí problém, je vidět
    // pořád, zbytek je na jedno klepnutí navíc.

    // ── Kde doklad leží ──
    { id: 'ke_schvaleni',   label: 'Ke schválení', skupina: 'Fronty', stavy: ['ke_schvaleni'], upozorneni: true },
    { id: 'ceka_na_platbu', label: 'Čeká platbu',  skupina: 'Fronty', stavy: ['ceka_na_platbu'] },
    { id: 'zaplaceno',      label: 'Zaplaceno',    skupina: 'Fronty', stavy: ['zaplaceny', 'zauctovany'] },
    { id: 'all',            label: 'Vše',          skupina: 'Fronty', stavy: null },

    // ── Co je na dokladu špatně ──
    // Přesně tyhle otázky vedou člověka do Dokladů: co mě brzdí, co už mělo být
    // zaplacené a kde se AI netrefila. Počty počítá server (`diagnostika_counts`).
    // První dvě nesou vlastní barvu — jsou to jediné pohledy, které hlásí chybu,
    // ne přihrádku, a jediné dvě, co mají v liště zkratku.
    { id: 'blokuje',        label: 'Blokuje schválení', skupina: 'Co je špatně', diagnostika: 'blokuje', upozorneni: true, Icon: AlertCircle, barva: BAD },
    { id: 'po_splatnosti',  label: 'Po splatnosti',     skupina: 'Co je špatně', diagnostika: 'po_splatnosti', upozorneni: true, Icon: AlertCircle, barva: WARN },
    { id: 'ai_nejista',     label: 'AI si není jistá',  skupina: 'Co je špatně', diagnostika: 'ai_nejista' },

    // ── Co s ním mám udělat rukama ──
    // „Čeká platbu" říká, že doklad není zaplacený — neřekne, jestli už příkaz
    // leží v bankovnictví. To je přitom jediná otázka, kterou si administrátor
    // dokladů u banky klade, a bez odpovědi se faktura zadá dvakrát, nebo vůbec.
    // Pohledy jsou disjunktní a dohromady dají všechno, co banka dluží.
    { id: 'k_tisku',        label: 'K tisku',      skupina: 'Moje práce', podoba: 'k_tisku', upozorneni: true, Icon: Printer },
    { id: 'k_zadani_do_banky', label: 'K zadání do banky', skupina: 'Moje práce', diagnostika: 'k_zadani_do_banky', upozorneni: true, Icon: SendHorizontal, barva: OK },
    { id: 'zadano_ceka_banku', label: 'Zadáno, čeká na banku', skupina: 'Moje práce', diagnostika: 'zadano_ceka_banku', Icon: SendHorizontal },
];

/** Pohled, na který se spadne, když `filter` žádnému neodpovídá. */
const VYCHOZI_POHLED = POHLEDY.find(p => p.id === 'all');

// Orthogonální k frontám per stav — proto vlastní řádek filtrů.
const PODOBA_FILTRY = [
    { id: '',          label: 'Vše' },
    { id: 'k_tisku',   label: 'K tisku' },
    { id: 'fyzicky',   label: 'Papír máme' },
    { id: 'vytisteno', label: 'Vytištěno' },
];

// ── Filtr období ────────────────────────────────────────────────────────────
// Filtruje se přes datum, které je v řádku vidět (vystavení, jinak účetní) —
// filtr tak sedí na to, co uživatel čte. Předvolby pokrývají běžné dotazy
// („co přišlo tenhle měsíc"), vlastní rozsah jde doplnit v poli od–do.
const den = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const OBDOBI_PREDVOLBY = [
    { id: '', label: 'Vše', rozsah: () => ({ od: '', do: '' }) },
    {
        id: 'tento_mesic', label: 'Tento měsíc',
        rozsah: () => { const n = new Date(); return { od: den(new Date(n.getFullYear(), n.getMonth(), 1)), do: den(new Date(n.getFullYear(), n.getMonth() + 1, 0)) }; },
    },
    {
        id: 'minuly_mesic', label: 'Minulý měsíc',
        rozsah: () => { const n = new Date(); return { od: den(new Date(n.getFullYear(), n.getMonth() - 1, 1)), do: den(new Date(n.getFullYear(), n.getMonth(), 0)) }; },
    },
    {
        id: 'dnu30', label: 'Posledních 30 dní',
        rozsah: () => { const n = new Date(); const o = new Date(); o.setDate(o.getDate() - 29); return { od: den(o), do: den(n) }; },
    },
    {
        id: 'letos', label: 'Letos',
        rozsah: () => { const r = new Date().getFullYear(); return { od: `${r}-01-01`, do: `${r}-12-31` }; },
    },
];

/** Předvolba, které zadaný rozsah odpovídá (jinak '' = vlastní / vše). */
const predvolbaProRozsah = ({ od, do: doDatum }) =>
    OBDOBI_PREDVOLBY.find(p => { const r = p.rozsah(); return r.od === od && r.do === doDatum; })?.id ?? null;

// ── Živá aktualizace seznamu ────────────────────────────────────────────────
// Primární kanál je Reverb (`doklady.live`) — upload i změna stavu dorazí hned.
// Polling zůstává jen jako fallback, kdyby WebSocket neběžel: rychleji, dokud
// něco skenuje, jinak líně. Běží pouze nad viditelnou záložkou.
const POLL_SKENOVANI = 5000;
const POLL_KLID = 30000;
// Po realtime eventu ještě dosrovnat se serverem (počty ve frontách, členství
// řádku ve filtru). Debounce spojí dávku eventů z jednoho skenu do jednoho dotazu.
const RECONCILE_MS = 1200;
const FLASH_MS = 2200;
// Velikost stránky. Menší dávka = seznam je na obrazovce hned a zbytek se
// dotahuje průběžně při scrollování; dřív se čekalo na dvě stě řádků a pak
// ještě na tlačítko „Načíst dalších 200".
const STRANKA = 60;
// Strop serveru na jeden dotaz — dorovnání už načtené části dál nesahá.
const STRANKA_MAX = 200;
// Jak daleko před koncem seznamu se sáhne po další stránce.
const PREDSTIH_PX = 500;

// Pole, jejichž změna se pozná na řádku — jen ta rozsvítí zvýraznění.
const SLEDOVANA_POLE = [
    'stav', 'castka_s_dph', 'cislo_dokladu', 'variabilni_symbol', 'interni_cislo',
    'datum_vystaveni', 'datum_splatnosti', 'polozky_count', 'ai_confidence_avg',
    'bank_transakce_id',
];

// `dodavatel` je buď objekt (spárovaný) nebo text z dokladu — porovnáváme název.
const nazevDodavatele = (d) => (typeof d?.dodavatel === 'object' ? d?.dodavatel?.nazev : d?.dodavatel) || '';

const jeJinyRadek = (a, b) =>
    SLEDOVANA_POLE.some(k => String(a?.[k] ?? '') !== String(b?.[k] ?? ''))
    || nazevDodavatele(a) !== nazevDodavatele(b);

// Hlavička tabulky — sjednoceno s PnlTabulkou Výsledovky.
function Th({ children, align = 'right', pl = false }) {
    const a = align === 'left' ? 'text-left' : align === 'center' ? 'text-center' : 'text-right';
    return (
        <th className={`${pl ? 'px-4' : 'px-3'} py-2.5 text-[10.5px] font-bold uppercase tracking-wide ${a}`}
            style={{ color: 'var(--text-tertiary)' }}>{children}</th>
    );
}

export function DokladStatusBadge({ stav, size = 'sm' }) {
    const cfg = fazeDokladu(stav);
    const Icon = cfg.Icon;
    const padding = size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs';
    const iconSize = size === 'sm' ? 11 : 13;

    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full font-medium ${padding}`}
            style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}33` }}
        >
            <Icon size={iconSize} className={cfg.spin ? 'animate-spin' : ''} />
            {cfg.label}
        </span>
    );
}

// Zaškrtávátko pro výběr dokladu (hromadné akce). Vlastní, ať ladí s designem.
function Vyberka({ checked, indeterminate = false, onClick, size = 18 }) {
    const aktivni = checked || indeterminate;
    return (
        <button type="button" role="checkbox" aria-checked={checked}
            onClick={(e) => { e.stopPropagation(); onClick?.(e); }}
            className="flex-shrink-0 rounded flex items-center justify-center transition-colors"
            style={{ width: size, height: size, color: '#fff',
                border: `1.5px solid ${aktivni ? SECTION_COLOR : 'var(--border-default)'}`,
                background: aktivni ? SECTION_COLOR : 'transparent' }}>
            {checked ? <Check size={size - 5} strokeWidth={3} /> : indeterminate ? <Minus size={size - 5} strokeWidth={3} /> : null}
        </button>
    );
}

/** Za kolik dní je splatnost — pro větu, kterou člověk čte rychleji než datum. */
function splatnostSlovy(datum) {
    if (!datum) return null;
    const dnu = Math.round((new Date(datum).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / 86400000);
    if (dnu < 0) return { text: `${Math.abs(dnu)} ${sklonuj(Math.abs(dnu), ['den', 'dny', 'dní'])} po splatnosti`, hori: true };
    if (dnu === 0) return { text: 'splatnost dnes', hori: true };
    if (dnu === 1) return { text: 'splatnost zítra', hori: false };
    if (dnu <= 7) return { text: `splatnost za ${dnu} dní`, hori: false };
    return null;   // vzdálená splatnost v seznamu nikoho nezajímá
}

const STAVY_BEZ_SPLATNOSTI = ['zaplaceny', 'zauctovany', 'zamitnuty'];

/**
 * Co o placení ukázat v řádku. Zaplacený doklad nebo zadaná platba už
 * splatnost neřeší — místo „za 3 dny / po splatnosti" je důležité, kdy se
 * zaplatilo, případně kdy šel příkaz do banky.
 */
function platbaSlovy(d) {
    if (d.zaplaceno_at || d.stav === 'zaplaceny') {
        return { text: d.zaplaceno_at ? `zaplaceno ${fmtDate(d.zaplaceno_at)}` : 'zaplaceno', barva: OK };
    }
    if (d.platba_zadana_at) {
        return { text: `platba zadána ${fmtDate(d.platba_zadana_at)}`, barva: OK };
    }
    if (STAVY_BEZ_SPLATNOSTI.includes(d.stav)) return null;
    const s = splatnostSlovy(d.datum_splatnosti);
    return s && { text: s.text, barva: s.hori ? BAD : null, tucne: s.hori };
}

/**
 * Řádek dokladu v úzkém sloupci a na mobilu.
 *
 * Kostra podle Surovin: barevná miniatura, název jako první a největší, pod
 * ním **jedna** metařádka, hodnota vpravo. Dřív tu byly čtyři řádky a v nich
 * tři identifikátory v neproporcionálním písmu (interní číslo, číslo dokladu,
 * VS). Na telefonu nepotřebuješ ani jeden — potřebuješ dodavatele, částku
 * a jestli to hoří. Čísla jsou v detailu, kde se s nimi pracuje.
 */
function DokladKarta({ d, isAdmin, vybrany, zaskrtnut, flashTyp, onOpen, onToggle, cardStyle, kompaktni = false }) {
    const dodavatel = d.dodavatel?.nazev || d.dodavatel;
    const cfg = fazeDokladu(d.stav);
    const Ikona = cfg.Icon;
    const splatnost = platbaSlovy(d);
    const meta = fmtDate(d.datum_vystaveni || d.datum);

    return (
        <div onClick={onOpen} data-doklad-id={d.id}
            className={`flex items-center cursor-pointer active:opacity-80 transition-colors${flashTyp ? ` doklad-radek-${flashTyp}` : ''}`}
            style={{
                gap: 10, padding: kompaktni ? '10px 10px' : '8px 10px',
                borderLeft: `3px solid ${vybrany ? SECTION_COLOR : 'transparent'}`,
                borderBottom: '1px solid rgba(127,127,140,0.14)',
                background: vybrany ? `${SECTION_COLOR}14` : (zaskrtnut ? `${SECTION_COLOR}0a` : 'transparent'),
            }}>
            {/* Zaškrtávátko jen na širší obrazovce — hromadné akce na telefonu
                nikdo nedělá a čtverec ukusuje levý okraj, kde má začít informace. */}
            {isAdmin && !kompaktni && <Vyberka checked={zaskrtnut} onClick={onToggle} size={20} />}

            <span className="inline-flex items-center justify-center flex-shrink-0"
                style={{ width: kompaktni ? 38 : 34, height: kompaktni ? 38 : 34, borderRadius: 10, background: cfg.bg, color: cfg.color }}>
                <Ikona size={kompaktni ? 17 : 15} className={cfg.spin ? 'animate-spin' : ''} />
            </span>

            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <span className="font-semibold truncate" style={{ fontSize: kompaktni ? 14.5 : 13, color: 'var(--text-primary)' }}>
                        {dodavatel || <span className="italic" style={{ color: 'var(--text-tertiary)' }}>není spárován</span>}
                    </span>
                </div>
                <div className="flex items-center gap-1.5 truncate" style={{ fontSize: kompaktni ? 12 : 11, color: 'var(--text-tertiary)', marginTop: 1 }}>
                    <span className="truncate">{meta}</span>
                    {splatnost && (
                        <span style={{ color: splatnost.barva || 'var(--text-tertiary)', fontWeight: splatnost.tucne ? 600 : 400, flexShrink: 0 }}>
                            · {splatnost.text}
                        </span>
                    )}
                </div>
            </div>

            <div className="tabular-nums font-bold flex-shrink-0"
                style={{ fontSize: kompaktni ? 14.5 : 13, color: 'var(--text-primary)' }}>
                {fmtKratce(d.castka_s_dph)}
            </div>
        </div>
    );
}

export default function FinanceDokladyInbox({ user, cardStyle, isDark, onOpenDetail, selectedDokladId = null,
    rok, mesic, refreshSignal = 0, onManualCreate, onOpenSablony = null, onOpenReport = null,
    // Hustota podle normy: `siroky` = přehled přes celou šířku (mřížka),
    // jinak navigační sloupec vedle detailu (dvouřádková karta).
    siroky = true, kompaktni = false, mutedColor, inputStyle, rezervaDole = null,
    // Nástroje modulu (statistiky, report, šablony) — do hlavičky, ne do seznamu.
    nastroje = null, nastrojeAkce = null,
    // Diagnostiky ven a filtr zvenku: rozepsané „21 dokladů nejde schválit"
    // bydlí v panelu statistik vedle seznamu, ale počítá se tady — se všemi
    // filtry seznamu, aby čísla v obou sloupcích seděla. `filtrZvenku` je
    // signál `{ id, token }`; mění se token, ne id, aby šlo do téhož pohledu
    // skočit i podruhé.
    onDiagnostiky = null, filtrZvenku = null }) {
    const [doklady, setDoklady] = useState([]);
    const [stavCounts, setStavCounts] = useState({});
    const [loading, setLoading] = useState(true);      // spinner jen při prvním načtení
    const [obnovuje, setObnovuje] = useState(false);   // tichá aktualizace na pozadí
    const [zvyraznene, setZvyraznene] = useState({});  // id → 'novy#token' | 'zmena#token'
    const [filter, setFilter] = useState('all');
    const [podoba, setPodoba] = useState('');   // fyzická podoba (k tisku / papír máme / vytištěno)
    const [podobaCounts, setPodobaCounts] = useState({});
    const [diagCounts, setDiagCounts] = useState({});
    const [celkem, setCelkem] = useState(null);     // poctivý počet za filtr (ne jen načtená stránka)
    const [dalsi, setDalsi] = useState(false);      // je co dotáhnout?
    const [dotahuje, setDotahuje] = useState(false);
    const [razeni, setRazeni] = useState('');       // '' = podle data dokladu
    const [tiskBusy, setTiskBusy] = useState(false);
    const [search, setSearch] = useState('');
    const [obdobi, setObdobi] = useState({ od: '', do: '' }); // filtr období (dle data v řádku)
    const [uploadError, setUploadError] = useState(null);
    const [stagedFiles, setStagedFiles] = useState(null); // soubory čekající ve staging okně
    const [vybrane, setVybrane] = useState(() => new Set()); // ID vybraných dokladů (hromadné akce)
    const [bulkBusy, setBulkBusy] = useState(false);
    const [bulkVysledek, setBulkVysledek] = useState(null);
    // Vybrané doklady se do modalu odesílání zkopírují — výběr se po odeslání
    // vyprázdní a modal by jinak zmizel dřív, než uživatel uvidí potvrzení.
    const [mailOpen, setMailOpen] = useState(null);
    const [aboOpen, setAboOpen] = useState(null);   // ids pro export do banky
    const fileInputRef = useRef(null);

    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const isAdmin = user?.role === 'superadmin' || user?.role === 'admin';

    const aktualniFiltr = POHLEDY.find(f => f.id === filter) || VYCHOZI_POHLED;

    // Diagnostiky nahoru do panelu statistik (třetí sloupec) — ten je umí jen
    // zobrazit, spočítat je musí seznam, protože je počítá server ke stejnému
    // dotazu, jaký seznam právě drží.
    useEffect(() => { onDiagnostiky?.(diagCounts); }, [diagCounts, onDiagnostiky]);

    // Klik na řádek v panelu statistik přepne pohled tady. Sleduje se token,
    // ne id — jinak by druhý klik na tentýž pohled (po ruční změně filtru)
    // neudělal nic.
    useEffect(() => {
        if (filtrZvenku?.id) setFilter(filtrZvenku.id);
    }, [filtrZvenku?.token, filtrZvenku?.id]);

    // Aktuální seznam i mimo render (diff v callbacku Echa nesmí zavírat starý stav)
    const dokladyRef = useRef([]);
    useEffect(() => { dokladyRef.current = doklady; }, [doklady]);

    // ── Zvýraznění řádku ────────────────────────────────────────────────────
    // Token v hodnotě mění React key řádku → CSS animace se rozjede znovu i při
    // dvou změnách těsně po sobě (jinak by druhá neproblikla).
    const flashTokenRef = useRef(0);
    const flashTimeryRef = useRef({});

    const flashni = useCallback((zmeny) => {
        const ids = Object.keys(zmeny);
        if (ids.length === 0) return;
        const t = ++flashTokenRef.current;
        setZvyraznene(prev => {
            const dalsi = { ...prev };
            ids.forEach(id => { dalsi[id] = `${zmeny[id]}#${t}`; });
            return dalsi;
        });
        ids.forEach(id => {
            clearTimeout(flashTimeryRef.current[id]);
            flashTimeryRef.current[id] = setTimeout(() => {
                delete flashTimeryRef.current[id];
                setZvyraznene(prev => {
                    const dalsi = { ...prev };
                    delete dalsi[id];
                    return dalsi;
                });
            }, FLASH_MS);
        });
    }, []);

    useEffect(() => () => Object.values(flashTimeryRef.current).forEach(clearTimeout), []);

    const zvyrazniZmeny = useCallback((stare, nove) => {
        if (stare.length === 0) return; // první naplnění seznamu není "změna"
        const mapa = new Map(stare.map(d => [d.id, d]));
        const zmeny = {};
        nove.forEach(d => {
            const stary = mapa.get(d.id);
            if (!stary) zmeny[d.id] = 'novy';
            else if (jeJinyRadek(stary, d)) zmeny[d.id] = 'zmena';
        });
        flashni(zmeny);
    }, [flashni]);

    // Hromadný tisk — server slije přílohy vybraných dokladů do jednoho PDF,
    // otevře se v nové záložce a rovnou nabídne tisk. Doklady, jejichž příloha
    // se do PDF opravdu dostala, si backend označí jako vytištěné.
    const vytisknoutVybrane = async () => {
        setTiskBusy(true);
        setBulkVysledek(null);
        try {
            const r = await fetch('/api/finance/doklady/tisk', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
                body: JSON.stringify({ ids: [...vybrane] }),
            });
            if (!r.ok) {
                const e = await r.json().catch(() => ({}));
                setBulkVysledek({ chyba: e.message || 'Tisk se nepodařilo připravit.' });
                return;
            }
            const preskoceno = decodeURIComponent(r.headers.get('X-Preskoceno') || '');
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            // Tisk z skrytého iframu — uživatel nemusí nic stahovat ani přepínat záložku.
            const ram = document.createElement('iframe');
            ram.style.display = 'none';
            ram.src = url;
            ram.onload = () => {
                try { ram.contentWindow.focus(); ram.contentWindow.print(); } catch { window.open(url, '_blank'); }
            };
            document.body.appendChild(ram);
            // Blob i iframe uklidíme, až uživatel dialog zavře (tisk je asynchronní).
            setTimeout(() => { URL.revokeObjectURL(url); ram.remove(); }, 120000);

            setBulkVysledek({
                zprava: `Připraveno k tisku: ${vybrane.size} dokladů.`,
                chyby: preskoceno ? preskoceno.split(' | ').filter(Boolean) : [],
            });
            setVybrane(new Set());
            loadData({ silent: true });
        } catch {
            setBulkVysledek({ chyba: 'Síťová chyba při přípravě tisku.' });
        } finally {
            setTiskBusy(false);
        }
    };

    // Tisk selhal / papír se ztratil — vrátit doklady zpět mezi „k tisku".
    const zrusitVytisteni = async () => {
        setTiskBusy(true);
        try {
            await fetch('/api/finance/doklady/oznacit-vytisteno', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
                body: JSON.stringify({ ids: [...vybrane], vytisteno: false }),
            });
            setVybrane(new Set());
            loadData({ silent: true });
        } catch {
            setBulkVysledek({ chyba: 'Nepodařilo se zrušit příznak vytištění.' });
        } finally {
            setTiskBusy(false);
        }
    };

    /**
     * Razítko „zadáno do banky" pro celý výběr. Platby se zadávají v dávce —
     * člověk odklikne příkazy v bankovnictví a pak jedním tahem označí, co
     * zadal. Server přeskočí doklady, na které razítko nepatří (hotovost,
     * už vyřízené), a řekne které — jinak by dávka tvrdila „hotovo" a část
     * výběru zůstala bez označení.
     */
    const oznacitPlatbuZadanou = async (zadano) => {
        setBulkBusy(true);
        try {
            const r = await fetch('/api/finance/doklady/oznacit-platbu-zadanou', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
                body: JSON.stringify({ ids: [...vybrane], zadano }),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) { setBulkVysledek({ chyba: data.error || 'Označení platby selhalo.' }); return; }
            const n = data.zmeneno ?? 0;
            setBulkVysledek({
                zprava: zadano
                    ? `Označeno jako zadané do banky: ${n} ${sklonuj(n, ['doklad', 'doklady', 'dokladů'])}.`
                    : `Zrušeno označení u ${n} ${sklonuj(n, ['dokladu', 'dokladů', 'dokladů'])}.`,
                chyby: data.preskoceno || [],
            });
            setVybrane(new Set());
            loadData({ silent: true });
        } catch {
            setBulkVysledek({ chyba: 'Síťová chyba při označování platby.' });
        } finally {
            setBulkBusy(false);
        }
    };

    // ── Načtení dat ─────────────────────────────────────────────────────────

    /**
     * Dotazovací parametry aktuálního pohledu a filtrů. Jedno místo pro první
     * načtení, dorovnání i dotahování dalších stránek — dřív to byly dvě kopie
     * téhož výčtu a rozejít se mohly kdykoli.
     */
    const paramyFiltru = useCallback(() => {
        const f = POHLEDY.find(x => x.id === filter) || VYCHOZI_POHLED;
        const params = new URLSearchParams();
        if (f.stavy) params.set('stav', f.stavy.join(','));
        if (search) params.set('search', search);
        // Pohled, který si podobu určuje sám („K tisku"), přebíjí filtr —
        // jinak by dvě nastavení téhož tiše vyrušila jedno druhé.
        if (f.diagnostika) params.set('diagnostika', f.diagnostika);
        if (f.podoba) params.set('podoba', f.podoba);
        else if (podoba) params.set('podoba', podoba);
        if (razeni) params.set('razeni', razeni);
        if (obdobi.od) params.set('od', obdobi.od);
        if (obdobi.do) params.set('do', obdobi.do);
        return params;
    }, [filter, search, podoba, razeni, obdobi]);

    /** Identita zobrazené sady — její změna znamená jiný seznam, ne dorovnání téhož. */
    const filtrKlic = useMemo(() => paramyFiltru().toString(), [paramyFiltru]);

    // silent = bez celoplošného spinneru (seznam zůstane na místě a jen se v něm
    // přepíšou dotčené buňky), flash = zvýraznit řádky, které se opravdu změnily.
    const reqIdRef = useRef(0);
    const klicRef = useRef(null);   // ze které sady jsou data, co v seznamu leží

    const loadData = useCallback(({ silent = false, flash = false } = {}) => {
        if (!silent) setLoading(true);
        setObnovuje(true);
        const mojeCislo = ++reqIdRef.current;

        // Jiná sada (přepnutý pohled, jiný filtr, jiné hledání) → seznam se
        // nahradí první stránkou. Tatáž sada → je to dorovnání, takže se
        // natáhne tolik řádků, kolik je načteno, aby průběžně dotažené stránky
        // nezmizely pod rukama.
        const jinaSada = klicRef.current !== filtrKlic;
        const kolik = jinaSada
            ? STRANKA
            : Math.min(Math.max(dokladyRef.current.length, STRANKA), STRANKA_MAX);

        const params = paramyFiltru();
        params.set('offset', 0);
        params.set('limit', kolik);

        return fetch(`/api/finance/doklady/inbox?${params}`)
            .then(r => r.json())
            .then(data => {
                // Opožděná odpověď staršího dotazu by přepsala novější data
                if (mojeCislo !== reqIdRef.current) return;
                const nove = data.doklady || [];
                // Zvýraznění má smysl jen mezi dvěma verzemi TÉŽE sady; po
                // přepnutí pohledu by rozsvítilo celý seznam.
                if (flash && !jinaSada) zvyrazniZmeny(dokladyRef.current, nove);
                klicRef.current = filtrKlic;
                setDoklady(stare => {
                    // Přepnutí pohledu = jiná sada, seznam se nahradí. Dřív se
                    // „dorovnával" i tady, takže v něm po zúžení zůstal zbytek
                    // té předchozí: pilulka hlásila sedm dokladů a pod ní jich
                    // stálo dvě stě. Dorovnání téže sady si naopak průběžně
                    // dotažené řádky pod první stránkou ponechá.
                    if (jinaSada || stare.length <= kolik) return nove;
                    const jizJe = new Set(nove.map(d => d.id));
                    return [...nove, ...stare.slice(kolik).filter(d => !jizJe.has(d.id))];
                });
                setCelkem(data.celkem ?? nove.length);
                setDalsi(!!data.dalsi);
                setStavCounts(data.stav_counts || {});
                setPodobaCounts(data.podoba_counts || {});
                setDiagCounts(data.diagnostika_counts || {});
                setLoading(false);
                setObnovuje(false);
            })
            .catch(() => {
                if (mojeCislo !== reqIdRef.current) return;
                setLoading(false);
                setObnovuje(false);
            });
    }, [paramyFiltru, filtrKlic, zvyrazniZmeny]);

    // První načtení (se spinnerem), přepnutí fronty a hledání (už jen tiše).
    // Psaní do hledání se debouncuje, přepnutí fronty musí reagovat okamžitě.
    const prvniNacteniRef = useRef(true);
    const prevSearchRef = useRef(search);
    useEffect(() => {
        const zmenaHledani = prevSearchRef.current !== search;
        prevSearchRef.current = search;
        const zpozdeni = zmenaHledani ? 350 : 0;
        const id = setTimeout(() => {
            loadData({ silent: !prvniNacteniRef.current });
            prvniNacteniRef.current = false;
        }, zpozdeni);
        return () => clearTimeout(id);
        // eslint-disable-next-line
    }, [filter, search, podoba, obdobi, razeni]);

    // Reload po vnější změně (např. po zavření detailu ručně vytvořeného dokladu)
    useEffect(() => {
        if (refreshSignal) loadData({ silent: true, flash: true });
        // eslint-disable-next-line
    }, [refreshSignal]);

    // ── Realtime přes Reverb (kanál doklady.live) ───────────────────────────
    const reconcileRef = useRef(null);
    const naplanujReconcile = useCallback(() => {
        clearTimeout(reconcileRef.current);
        reconcileRef.current = setTimeout(() => loadData({ silent: true, flash: true }), RECONCILE_MS);
    }, [loadData]);

    useEffect(() => {
        if (!window.Echo) return;
        let kanal;
        try {
            kanal = window.Echo.private('doklady.live');
        } catch (e) {
            return; // bez WebSocketu dojede fallback polling níž
        }

        // Změna existujícího dokladu → přepsat JEN jeho řádek, zbytek tabulky
        // se nesahá. Reconcile pak dosrovná počty a případný odchod z fronty.
        const naZmenu = (e) => {
            const row = e?.doklad;
            if (!row) return;
            const stary = dokladyRef.current.find(d => d.id === row.id);
            if (stary) {
                if (jeJinyRadek(stary, row)) flashni({ [row.id]: 'zmena' });
                setDoklady(prev => prev.map(d => (d.id === row.id ? { ...d, ...row } : d)));
            }
            naplanujReconcile();
        };

        // Nový doklad — refetch (řádek musí projít filtrem i řazením serveru),
        // diff mu pak rozsvítí zvýraznění "novy".
        const naNovy = () => naplanujReconcile();

        const naSmazani = (e) => {
            if (e?.id) setDoklady(prev => prev.filter(d => d.id !== e.id));
            naplanujReconcile();
        };

        kanal.listen('.DokladZmenen', naZmenu);
        kanal.listen('.NovyDoklad', naNovy);
        kanal.listen('.DokladSmazan', naSmazani);

        // Jen odhlásit posluchače — kanál sdílíme s app.jsx (toast), leave() by
        // ho shodil i jemu.
        return () => {
            kanal.stopListening('.DokladZmenen', naZmenu);
            kanal.stopListening('.NovyDoklad', naNovy);
            kanal.stopListening('.DokladSmazan', naSmazani);
            clearTimeout(reconcileRef.current);
        };
    }, [naplanujReconcile, flashni]);

    // ── Fallback polling ────────────────────────────────────────────────────
    // Pojistka, kdyby Reverb neběžel. Nad skrytou záložkou se nedotazuje vůbec.
    const probihaSken = doklady.some(d => d.stav === 'nahrany' || d.stav === 'skenuje_se');
    useEffect(() => {
        const id = setInterval(() => {
            if (document.visibilityState === 'visible') loadData({ silent: true, flash: true });
        }, probihaSken ? POLL_SKENOVANI : POLL_KLID);
        return () => clearInterval(id);
    }, [probihaSken, loadData]);

    // Návrat k oknu — dorovnat hned, ať se nekouká na data stará půl hodiny
    useEffect(() => {
        const naNavrat = () => {
            if (document.visibilityState === 'visible') loadData({ silent: true, flash: true });
        };
        document.addEventListener('visibilitychange', naNavrat);
        window.addEventListener('focus', naNavrat);
        return () => {
            document.removeEventListener('visibilitychange', naNavrat);
            window.removeEventListener('focus', naNavrat);
        };
    }, [loadData]);

    // Výběr souborů → otevři staging okno (seskupení + async upload)
    const handleUpload = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        setUploadError(null);
        setStagedFiles(files);
    };

    const zavritStaging = () => {
        setStagedFiles(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    /** Počet v pilulce pohledu — ze serverových součtů, ne z načtené stránky. */
    const pocetPohledu = (p) => {
        if (p.diagnostika) return diagCounts[p.diagnostika] ?? 0;
        if (p.podoba) return podobaCounts[p.podoba] ?? 0;
        if (!p.stavy) return Object.values(stavCounts).reduce((s, n) => s + n, 0);
        return p.stavy.reduce((s, k) => s + (stavCounts[k] || 0), 0);
    };

    // Kolik dokladů aktuální pohled vůbec má — rozliší „nic nenašel filtr" od
    // „fronta je prázdná". Diagnostické pohledy sem dřív nespadaly a hlásily
    // součet všech stavů.
    const filtrTotal = pocetPohledu(aktualniFiltr);

    // ── Výběr + hromadné akce ────────────────────────────────────────────────
    // Výběr se čistí jen při změně fronty/hledání (mění se viditelná sada) — NE při
    // tichém pollingu/realtime, jinak by ho auto-refresh mazal uprostřed vybírání.
    useEffect(() => { setVybrane(new Set()); }, [filter, search, podoba, obdobi, razeni]);

    // Klávesové zkratky v otevřeném detailu: ↑/↓ přepínají mezi doklady, mezerník
    // označí/odznačí aktuální doklad pro hromadnou akci. Aktivní jen s otevřeným
    // detailem (jinak šipky/mezerník necháme na normální scroll). Nehijackujeme
    // psaní v inputech/dropdownech ani tlačítka v otevřeném okně.
    useEffect(() => {
        if (selectedDokladId == null) return;
        const onKey = (e) => {
            if (e.target?.closest?.('input, textarea, select, [contenteditable="true"], [role="listbox"], [role="combobox"]')) return;
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                const idx = doklady.findIndex(d => d.id === selectedDokladId);
                if (idx === -1) return;
                const ni = e.key === 'ArrowDown' ? Math.min(idx + 1, doklady.length - 1) : Math.max(idx - 1, 0);
                if (ni !== idx) { e.preventDefault(); onOpenDetail?.(doklady[ni].id); }
            } else if ((e.key === ' ' || e.code === 'Space') && isAdmin) {
                // Po kliknutí na řádek/filtr zůstane fokus na tlačítku a mezerník
                // by ho „zmáčkl" místo výběru (2026-10-14). V okně (modal)
                // tlačítka necháme být, jinde fokus shodíme a vybíráme.
                if (e.target?.closest?.('[role="dialog"]')) return;
                if (e.target?.closest?.('button, a')) e.target.blur?.();
                e.preventDefault();
                setVybrane(prev => {
                    const s = new Set(prev);
                    s.has(selectedDokladId) ? s.delete(selectedDokladId) : s.add(selectedDokladId);
                    return s;
                });
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [selectedDokladId, doklady, onOpenDetail, isAdmin]);

    // Vybraný řádek dorolovat do zorného pole (klik i šipky); nescrolluje, když je vidět.
    useEffect(() => {
        if (selectedDokladId == null) return;
        document.querySelector(`[data-doklad-id="${selectedDokladId}"]`)?.scrollIntoView({ block: 'nearest' });
    }, [selectedDokladId]);

    const prepnoutVyber = (id) => setVybrane(prev => {
        const s = new Set(prev);
        s.has(id) ? s.delete(id) : s.add(id);
        return s;
    });
    // Pořadí ze seznamu drží i modal odesílání — ať odpovídá tomu, co je vidět.
    const vybraneDoklady = doklady.filter(d => vybrane.has(d.id));
    const vsechnyVybrane = doklady.length > 0 && doklady.every(d => vybrane.has(d.id));
    const nekterVybran = doklady.some(d => vybrane.has(d.id));
    const prepnoutVsechny = () => setVybrane(
        doklady.every(d => vybrane.has(d.id)) ? new Set() : new Set(doklady.map(d => d.id))
    );

    const AKCE_LABEL = { schvalit: 'Schválit', zamitnout: 'Zamítnout', rescan: 'Rescan', smazat: 'Smazat' };
    const spustitHromadnou = async (akce) => {
        const ids = Array.from(vybrane);
        if (ids.length === 0) return;
        const pocet = `${ids.length} ${ids.length === 1 ? 'doklad' : ids.length < 5 ? 'doklady' : 'dokladů'}`;
        let duvod = null;
        if (akce === 'zamitnout') {
            duvod = window.prompt(`Důvod zamítnutí (${pocet}, min 3 znaky):`);
            if (!duvod || duvod.trim().length < 3) return;
        } else if (!window.confirm(`${AKCE_LABEL[akce]} ${pocet}?`)) {
            return;
        }
        setBulkBusy(true); setBulkVysledek(null);
        try {
            const r = await fetch('/api/finance/doklady/hromadna-akce', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
                body: JSON.stringify({ akce, ids, ...(duvod ? { duvod: duvod.trim() } : {}) }),
            });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) setBulkVysledek({ chyba: d.message || 'Hromadná akce selhala' });
            else {
                setBulkVysledek({ akce, ok: d.ok, celkem: d.celkem, chyby: d.chyby || [], cenik: d.cenik });
                setVybrane(new Set());
                loadData({ silent: true, flash: true });
            }
        } catch { setBulkVysledek({ chyba: 'Síťová chyba' }); }
        setBulkBusy(false);
    };

    // ── Průběžné dotahování ──────────────────────────────────────────────────
    // Další stránka se přidává za načtené, nenahrazuje je. Spouští ji strážce
    // pod posledním řádkem, ne tlačítko.
    const dotahujeRef = useRef(false);

    const dotahniDalsi = useCallback(async () => {
        if (dotahujeRef.current) return;      // strážce umí hlásit vícekrát za sebou
        dotahujeRef.current = true;
        setDotahuje(true);
        try {
            const params = paramyFiltru();
            params.set('limit', STRANKA);
            params.set('offset', dokladyRef.current.length);
            const r = await fetch(`/api/finance/doklady/inbox?${params}`);
            const data = await r.json();
            // Mezitím se mohl přepnout pohled — odpověď staré sady zahodit.
            if (klicRef.current !== filtrKlic) return;
            setDoklady(d => {
                const mam = new Set(d.map(x => x.id));
                return [...d, ...(data.doklady || []).filter(x => !mam.has(x.id))];
            });
            setCelkem(data.celkem ?? null);
            setDalsi(!!data.dalsi);
        } catch { /* seznam zůstane, jak byl */ }
        finally {
            dotahujeRef.current = false;
            setDotahuje(false);
        }
    }, [paramyFiltru, filtrKlic]);

    /**
     * Strážce konce seznamu — jakmile se přiblíží, dotáhne se další stránka.
     * Seznam tak roste sám podle toho, kam člověk doroloval. Observer se po
     * každém přírůstku staví znovu: kdyby zůstal, druhý průnik už nenahlásí
     * a dotahování by se po jedné stránce zaseklo.
     */
    const strazceRef = useRef(null);
    const scrollRef = useRef(null);
    useEffect(() => {
        const cil = strazceRef.current;
        if (!cil || !dalsi || loading) return;
        const io = new IntersectionObserver(
            (zaznamy) => { if (zaznamy.some(z => z.isIntersecting)) dotahniDalsi(); },
            { root: scrollRef.current, rootMargin: `0px 0px ${PREDSTIH_PX}px 0px` },
        );
        io.observe(cil);
        return () => io.disconnect();
    }, [dalsi, loading, doklady.length, dotahniDalsi]);

    // Jiná sada = jiný seznam, takže se čte zase odshora. Bez tohohle se člověk
    // po přepnutí pohledu ocitne uprostřed kratšího seznamu.
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        // `Element.scrollTo` není všude (starší WebView v PWA) — `scrollTop` ano.
        if (typeof el.scrollTo === 'function') el.scrollTo({ top: 0 });
        else el.scrollTop = 0;
    }, [filtrKlic]);

    // ── Hlavička seznamu (norma) ─────────────────────────────────────────────

    // Odsazení pruhů uvnitř karty seznamu — hlavička má p-3 (na mobilu 8/10 px),
    // takže cokoli pod ní musí sedět na stejné svislici.
    const vnitrniOkraj = kompaktni ? 'mx-2.5' : 'mx-3';

    const akceToolbaru = [
        { id: 'nahrat', label: 'Nahrát doklady', Icon: Upload, onClick: () => fileInputRef.current?.click(), primarni: true },
        ...(isAdmin && onManualCreate ? [{ id: 'rucne', label: 'Vytvořit ručně', Icon: Plus, onClick: onManualCreate }] : []),
    ];

    // Zúžení sady — období vždy, fyzická podoba jen když ji nepředurčil pohled
    // („K tisku"), jinak by proti sobě stály dva ovladače téhož.
    const filtrSkupiny = [
        {
            // „Vše" musí být v nabídce vypsané, aby se dalo vybrat zpátky — ale je to
            // výchozí stav, ne zapnutý filtr (viz `vychozi` v `ListFiltr`).
            id: 'obdobi', typ: 'dropdown', label: 'Období', labelKratky: 'Období', vychozi: 'vse',
            options: [
                ...OBDOBI_PREDVOLBY.map(p => ({ value: p.id || 'vse', label: p.label })),
                { value: 'vlastni', label: 'Vlastní rozsah…' },
            ],
        },
        ...(aktualniFiltr.podoba ? [] : [{
            id: 'podoba', typ: 'dropdown', label: 'Fyzická podoba', labelKratky: 'Podoba',
            options: PODOBA_FILTRY.filter(f => f.id).map(f => ({ value: f.id, label: f.label })),
        }]),
    ];

    const filtrHodnoty = {
        obdobi: (obdobi.od || obdobi.do) ? (predvolbaProRozsah(obdobi) || 'vlastni') : 'vse',
        podoba: podoba || '',
    };

    const nastavFiltr = (id, hodnota) => {
        if (id === 'obdobi') {
            if (hodnota === 'vlastni') return;                    // rozsah zadá člověk v polích pod hlavičkou
            const p = OBDOBI_PREDVOLBY.find(x => (x.id || 'vse') === (hodnota ?? 'vse'));
            setObdobi(p ? p.rozsah() : { od: '', do: '' });
            return;
        }
        if (id === 'podoba') setPodoba(hodnota ?? '');
    };
    const resetFiltru = () => { setObdobi({ od: '', do: '' }); setPodoba(''); };

    return (
        <div className="h-full flex flex-col min-h-0">
            {stagedFiles && (
                <DokladyUploadModal
                    soubory={stagedFiles}
                    cardStyle={cardStyle}
                    onClose={zavritStaging}
                    onNahrano={() => loadData({ silent: true, flash: true })}
                />
            )}
            {aboOpen && (
                <ExportAboModal
                    ids={aboOpen}
                    isDark={isDark}
                    onClose={() => setAboOpen(null)}
                    onHotovo={(d) => {
                        const n = d.platby.length;
                        setBulkVysledek({
                            zprava: `Export pro banku: ${n} ${sklonuj(n, ['platba', 'platby', 'plateb'])} (${d.nazev})`
                                + (d.oznaceno ? `, označeno jako zadané: ${d.oznaceno}.` : ' — zatím neoznačeno jako zadané.'),
                            chyby: d.preskoceno || [],
                        });
                        setVybrane(new Set());
                        if (d.oznaceno) loadData({ silent: true });
                    }}
                />
            )}
            {mailOpen && (
                <OdeslatMailemModal
                    doklady={mailOpen}
                    cardStyle={cardStyle}
                    onClose={() => setMailOpen(null)}
                    onOdeslano={(d) => {
                        setBulkVysledek({
                            zprava: `Odesláno na ${d.komu.join(', ')}: ${d.odeslano} ${sklonuj(d.odeslano, ['doklad', 'doklady', 'dokladů'])}, `
                                + `${d.priloh} ${sklonuj(d.priloh, ['příloha', 'přílohy', 'příloh'])} (${d.velikost}).`,
                            chyby: d.preskoceno || [],
                        });
                        setVybrane(new Set());
                    }}
                />
            )}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                multiple
                onChange={handleUpload}
                className="hidden"
            />

            {/* Seznam je JEDNA karta — hlavička, pruh úkolů i řádky na jedné
                ploše, jako u seznamu surovin. Dřív hlavička visela nad stránkou
                bez pozadí a kartu měla až tabulka pod ní, takže hrana pozadí
                padla doprostřed seznamu. Na mobilu karta není: seznam je celá
                obrazovka a sklo přes ni jen rozmaže brandovou záři pod sebou. */}
            <div className={`flex flex-col flex-1 min-h-0 overflow-hidden ${kompaktni ? '' : 'rounded-2xl'}`}
                style={kompaktni ? undefined : cardStyle}>

            {/* Hlavička seznamu podle normy (`standard-rozvrzeni.md`): hledání
                vlevo, akce vpravo, pohledy pod tím. Dřív to bylo naopak a pod
                sebou stály tři téměř stejné pruhy pilulek — fronty stavů,
                období, fyzická podoba. Období a podoba jsou zúžení sady, ne
                pohled, takže patří pod tlačítko filtru. */}
            <ListToolbar
                siroky={siroky} kompaktni={kompaktni} isDark={isDark}
                mutedColor={mutedColor} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{
                    value: search, onChange: setSearch,
                    placeholder: 'Hledat dodavatele, popis, VS, číslo…', placeholderKratky: 'Hledat',
                }}
                akce={akceToolbaru}
                nastroje={nastroje}
                nastrojeAkce={nastrojeAkce}
                filtr={(
                    <FiltrTlacitko
                        skupiny={filtrSkupiny} hodnoty={filtrHodnoty}
                        onChange={nastavFiltr} onReset={resetFiltru}
                        pocetVysledku={celkem ?? doklady.length}
                        siroky={siroky} kompaktni={kompaktni}
                        isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR}
                    />
                )}
                pohledy={POHLEDY.map(p => ({ ...p, pocet: pocetPohledu(p) }))}
                pohledAktivni={aktualniFiltr.id}
                onPohled={setFilter}
                aktivniFiltry={(
                    <AktivniFiltry skupiny={filtrSkupiny} hodnoty={filtrHodnoty}
                        onChange={nastavFiltr} onReset={resetFiltru}
                        isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} />
                )}
            />

            {/* Pruh „co je potřeba udělat" tu stával nad seznamem a bral mu dva
                řádky výšky pokaždé, když něco viselo — tedy prakticky pořád.
                Naléhavost teď nese barevný počet v pilulce (nula pixelů navíc)
                a rozepsané „21 dokladů nejde schválit" má místo ve třetím
                sloupci, v panelu statistik (`DokladyStatistikyPanel`). */}

            {/* Vlastní rozsah dat — jen když si ho člověk v filtru vybral. */}
            {filtrHodnoty.obdobi === 'vlastni' && (
                <div className={`shrink-0 flex items-center gap-1.5 ${vnitrniOkraj} mt-2`}>
                    <input type="date" value={obdobi.od} max={obdobi.do || undefined}
                        onChange={e => setObdobi(o => ({ ...o, od: e.target.value }))}
                        style={{ ...inputStyle, width: 'auto', padding: '6px 8px', fontSize: 12 }} title="Od (včetně)" />
                    <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>–</span>
                    <input type="date" value={obdobi.do} min={obdobi.od || undefined}
                        onChange={e => setObdobi(o => ({ ...o, do: e.target.value }))}
                        style={{ ...inputStyle, width: 'auto', padding: '6px 8px', fontSize: 12 }} title="Do (včetně)" />
                </div>
            )}

            {uploadError && (
                <div className={`${vnitrniOkraj} mt-2 p-3 rounded-lg flex items-start gap-2`} style={{ background: `${BAD}14`, border: `1px solid ${BAD}33` }}>
                    <AlertCircle size={16} style={{ color: BAD, marginTop: 2 }} />
                    <div className="text-sm" style={{ color: BAD }}>{uploadError}</div>
                </div>
            )}

            {/* Lišta hromadných akcí — když je něco vybráno.
                Jedna hlavní akce podle toho, co ve výběru je (schválit, nebo
                poslat do banky), zbytek pod „⋯" (2026-10-14: dřív devět
                tlačítek ve dvou řadách). Mazání a zamítnutí jsou až na konci
                nabídky, ať se netrefí omylem vedle Schválit. */}
            {isAdmin && vybrane.size > 0 && (() => {
                const keSchvaleni = vybraneDoklady.some(d => ['rozpoznan', 'k_doplneni', 'ke_schvaleni'].includes(d.stav));
                const kPlaceni = vybraneDoklady.some(d => ['schvaleny', 'ceka_na_platbu'].includes(d.stav) && !d.platba_zadana_at);
                const akce = [
                    keSchvaleni && { id: 'schvalit', label: 'Schválit', Icon: CheckCircle2, primarni: true, barva: OK, onClick: () => spustitHromadnou('schvalit') },
                    !keSchvaleni && kPlaceni && { id: 'abo', label: 'Export pro banku', Icon: Landmark, primarni: true, barva: SECTION_COLOR, onClick: () => setAboOpen([...vybrane]) },
                    keSchvaleni && kPlaceni && { id: 'abo', label: 'Export pro banku', Icon: Landmark, onClick: () => setAboOpen([...vybrane]) },
                    kPlaceni && { id: 'zadano', label: 'Označit: zadáno do banky', Icon: SendHorizontal, onClick: () => oznacitPlatbuZadanou(true) },
                    filter === 'zadano_ceka_banku' && { id: 'zrusit_zadani', label: 'Zrušit zadání do banky', Icon: RefreshCw, onClick: () => oznacitPlatbuZadanou(false) },
                    { id: 'tisk', label: 'Tisk', Icon: Printer, onClick: vytisknoutVybrane },
                    { id: 'mail', label: 'Odeslat mailem', Icon: Mail, onClick: () => setMailOpen(vybraneDoklady) },
                    podoba === 'vytisteno' && { id: 'zrusit_tisk', label: 'Zrušit vytištění', Icon: RefreshCw, onClick: zrusitVytisteni },
                    { id: 'rescan', label: 'Znovu přečíst (AI)', Icon: RefreshCw, onClick: () => spustitHromadnou('rescan') },
                    { id: 'zamitnout', label: 'Zamítnout', Icon: XCircle, barva: BAD, onClick: () => spustitHromadnou('zamitnout') },
                    { id: 'smazat', label: 'Smazat', Icon: Trash2, barva: BAD, onClick: () => spustitHromadnou('smazat') },
                ].filter(Boolean);
                return (
                    <div className={`shrink-0 flex items-center gap-2 ${vnitrniOkraj} mt-2 py-1.5 pl-3 pr-1.5 rounded-xl`}
                        style={{ background: `${SECTION_COLOR}0e`, border: `1px solid ${SECTION_COLOR}44` }}>
                        <span className="text-sm font-semibold" style={{ color: SECTION_COLOR }}>
                            {vybrane.size} {sklonuj(vybrane.size, ['vybraný', 'vybrané', 'vybraných'])}
                        </span>
                        <button onClick={() => setVybrane(new Set())} disabled={bulkBusy} title="Zrušit výběr" aria-label="Zrušit výběr"
                            className="inline-flex items-center justify-center rounded-md" style={{ width: 26, height: 26, color: 'var(--text-tertiary)' }}>
                            <XCircle size={15} />
                        </button>
                        <div className="flex-1 min-w-0" />
                        {bulkBusy || tiskBusy
                            ? <Loader2 size={16} className="animate-spin mx-2" style={{ color: SECTION_COLOR }} />
                            : <NabidkaAkci akce={akce} kolikVidet={1} isMobile={kompaktni} isDark={isDark} barva={SECTION_COLOR} />}
                    </div>
                );
            })()}

            {/* Výsledek poslední hromadné akce */}
            {bulkVysledek && (
                <div className={`shrink-0 ${vnitrniOkraj} mt-2 p-2.5 rounded-xl text-sm flex items-start gap-2`}
                    style={{ background: bulkVysledek.chyba || bulkVysledek.chyby?.length ? `${WARN}12` : `${OK}12`,
                        border: `1px solid ${bulkVysledek.chyba || bulkVysledek.chyby?.length ? WARN : OK}44` }}>
                    <div className="flex-1" style={{ color: 'var(--text-secondary)' }}>
                        {bulkVysledek.chyba
                            ? <span style={{ color: BAD }}>{bulkVysledek.chyba}</span>
                            : <>
                                {/* Tisk a odeslání hlásí větou, workflow akce poměrem OK/celkem. */}
                                <b style={{ color: OK }}>
                                    {bulkVysledek.zprava ?? `${AKCE_LABEL[bulkVysledek.akce]}: ${bulkVysledek.ok}/${bulkVysledek.celkem} OK`}
                                </b>
                                {/* Schválení zapisuje nákupní ceny do ceníků — ať je vidět, co udělalo. */}
                                {cenikShrnuti(bulkVysledek.cenik) && (
                                    <div className="mt-0.5 text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                        Ceník: {cenikShrnuti(bulkVysledek.cenik)}
                                    </div>
                                )}
                                {bulkVysledek.chyby.length > 0 && (
                                    <ul className="mt-1 text-xs list-disc list-inside" style={{ color: WARN }}>
                                        {bulkVysledek.chyby.slice(0, 5).map((c, i) => (
                                            <li key={i}>{typeof c === 'string' ? c : `#${c.id}: ${c.error}`}</li>
                                        ))}
                                        {bulkVysledek.chyby.length > 5 && <li>…a dalších {bulkVysledek.chyby.length - 5}</li>}
                                    </ul>
                                )}
                            </>}
                    </div>
                    <button onClick={() => setBulkVysledek(null)} style={{ color: 'var(--text-tertiary)' }}><XCircle size={16} /></button>
                </div>
            )}

            {/* Tabulka i karty scrollují v JEDNOM kontejneru — sticky hlavička
                tabulky i strážce průběžného dotahování potřebují společný kořen
                scrollu. */}
            <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto"
                style={rezervaDole ? { paddingBottom: rezervaDole, overscrollBehavior: 'contain' } : undefined}>
                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <Loader2 size={24} className="animate-spin" style={{ color: SECTION_COLOR }} />
                    </div>
                ) : doklady.length === 0 ? (
                    <div className="text-center py-12">
                        <FileText size={32} className="mx-auto mb-2" style={{ color: 'var(--text-tertiary)' }} />
                        <div className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                            {search || obdobi.od || obdobi.do || podoba
                                ? 'Žádné výsledky pro zadaný filtr'
                                : 'Žádné doklady v této frontě'}
                        </div>
                        {filtrTotal === 0 && !search && !obdobi.od && !obdobi.do && !podoba && (
                            <div className="text-xs mt-2" style={{ color: 'var(--text-tertiary)' }}>
                                Nahrajte první doklad tlačítkem výše
                            </div>
                        )}
                    </div>
                ) : siroky ? (
                    /* Přehled přes celou šířku — tabulka; hlavička zůstává (sticky) */
                    <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                        <thead>
                            {/* Neprůhledná plocha — hlavička je lepivá, takže pod ní
                                jedou řádky. Je to týž stupeň, který od 3.480.0 vrací
                                `tableSurfaces().headBg`; předtím byl token modrý,
                                tak si to tahle tabulka brala z CSS napřímo. */}
                            <tr style={{ background: 'var(--surface-elevated)', position: 'sticky', top: 0, zIndex: 10 }}>
                                {isAdmin && (
                                    <th className="px-4 py-2.5" style={{ width: 44 }}>
                                        <Vyberka checked={vsechnyVybrane} indeterminate={!vsechnyVybrane && nekterVybran} onClick={prepnoutVsechny} size={17} />
                                    </th>
                                )}
                                <Th align="left" pl={!isAdmin}>Stav</Th>
                                <Th align="left">Dodavatel</Th>
                                <Th align="left">Č. dokladu / VS</Th>
                                <Th align="left">Datum / Splatnost</Th>
                                <Th>Částka</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {doklady.map(d => {
                                const vybrany = selectedDokladId === d.id;
                                const zaskrtnut = vybrane.has(d.id);
                                // 'novy#12' → typ zvýraznění; token v key restartuje animaci
                                const flash = zvyraznene[d.id];
                                const flashTyp = flash ? flash.split('#')[0] : null;
                                return (
                                <tr
                                    key={flash ? `${d.id}:${flash}` : d.id}
                                    data-doklad-id={d.id}
                                    onClick={() => onOpenDetail?.(d.id)}
                                    className={`cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 transition-colors${flashTyp ? ` doklad-radek-${flashTyp}` : ''}`}
                                    style={{
                                        borderBottom: '1px solid var(--border-subtle)',
                                        background: vybrany ? `${SECTION_COLOR}14` : (zaskrtnut ? `${SECTION_COLOR}0a` : undefined),
                                        boxShadow: vybrany ? `inset 3px 0 0 ${SECTION_COLOR}` : undefined,
                                    }}
                                >
                                    {isAdmin && (
                                        <td className="px-4 py-2"><Vyberka checked={zaskrtnut} onClick={() => prepnoutVyber(d.id)} size={17} /></td>
                                    )}
                                    <td className={isAdmin ? 'px-3 py-2' : 'px-4 py-2'}><DokladStatusBadge stav={d.stav} /></td>
                                    <td className="px-3 py-2" style={{ color: 'var(--text-primary)' }}>
                                        <div className="font-medium">{d.dodavatel?.nazev || d.dodavatel || <span style={{ color: 'var(--text-tertiary)' }} className="italic">není spárován</span>}</div>
                                        {d.dodavatel?.ico && (
                                            <div className="text-[10px] font-mono" style={{ color: 'var(--text-tertiary)' }}>IČO {d.dodavatel.ico}</div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 font-mono text-xs" style={{ color: 'var(--text-primary)' }}>
                                        {d.interni_cislo && (
                                            <div style={{ color: 'var(--text-secondary)', fontWeight: 700 }}>{d.interni_cislo}</div>
                                        )}
                                        <div style={{ color: 'var(--text-tertiary)' }}>č. {d.cislo_dokladu || '—'}</div>
                                        {d.variabilni_symbol && (
                                            <div style={{ color: 'var(--text-tertiary)' }}>VS {d.variabilni_symbol}</div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-xs" style={{ color: 'var(--text-primary)' }}>
                                        <div>{fmtDate(d.datum_vystaveni || d.datum)}</div>
                                        {d.zaplaceno_at || d.stav === 'zaplaceny' ? (
                                            <div style={{ color: OK }}>zaplaceno{d.zaplaceno_at ? ` ${fmtDate(d.zaplaceno_at)}` : ''}</div>
                                        ) : d.platba_zadana_at ? (
                                            <div style={{ color: OK }}>zadáno {fmtDate(d.platba_zadana_at)}</div>
                                        ) : d.datum_splatnosti && !STAVY_BEZ_SPLATNOSTI.includes(d.stav) && (
                                            <div style={{ color: 'var(--text-tertiary)' }}>do {fmtDate(d.datum_splatnosti)}</div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums font-semibold" style={{ color: 'var(--text-primary)' }}>
                                        {fmt(d.castka_s_dph)} Kč
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                ) : (
                    /* Úzký sloupec vedle detailu a mobil — dvouřádkové karty */
                    doklady.map(d => {
                        const flash = zvyraznene[d.id];
                        return (
                            <DokladKarta key={flash ? `${d.id}:${flash}` : d.id} d={d} isAdmin={isAdmin}
                                vybrany={selectedDokladId === d.id} zaskrtnut={vybrane.has(d.id)}
                                flashTyp={flash ? flash.split('#')[0] : null}
                                onOpen={() => onOpenDetail?.(d.id)} onToggle={() => prepnoutVyber(d.id)}
                                cardStyle={cardStyle} kompaktni={kompaktni} />
                        );
                    })
                )}

                {/* Konec seznamu — a zároveň strážce, který dotáhne další
                    stránku. Dřív tu stálo tlačítko „Načíst dalších 200": seznam
                    se usekl a čekal, až o zbytek někdo požádá. */}
                {!loading && doklady.length > 0 && (
                    <div ref={strazceRef} className="flex items-center justify-center gap-2 py-3 text-xs"
                        style={{ color: 'var(--text-tertiary)' }}>
                        {dalsi ? (
                            <>
                                {dotahuje && <Loader2 size={13} className="animate-spin" />}
                                <span>{doklady.length} z {celkem}</span>
                            </>
                        ) : (
                            <span>{celkem ?? doklady.length} {sklonuj(celkem ?? doklady.length, ['doklad', 'doklady', 'dokladů'])}</span>
                        )}
                    </div>
                )}
            </div>

            {/* konec karty seznamu */}
            </div>

            {/* Nápověda ke zkratkám (jen s otevřeným detailem) */}
            {!loading && isAdmin && selectedDokladId != null && siroky && (
                <div className="shrink-0 text-xs mt-3 flex flex-wrap items-center gap-x-3 gap-y-1" style={{ color: 'var(--text-tertiary)' }}>
                    <span className="hidden sm:inline">
                        Tip: <b>↑ ↓</b> přepínají doklady, <b>mezerník</b> označí pro hromadnou akci
                    </span>
                </div>
            )}
        </div>
    );
}
